import { ProductType } from 'cart-model';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Screen from '../components/ProductWizard/Screen';
import { logSelectItemEvent, logViewItemEvent } from './events';
import ProductWizardState from './ProductWizardState';

const FundEventsContext = createContext<{
  logFundVisibilityIfShownFirstTime: (fundId: number, productType: ProductType, index: number) => void,
  logFundSelection: (fundId: number, productType: ProductType, index: number, riskProfilerLevel?: number) => void
}>({ logFundVisibilityIfShownFirstTime: () => {}, logFundSelection: () => {} });

export const useFundEventLoggerWithContext = () => {
  const {logFundVisibilityIfShownFirstTime, logFundSelection} = useContext(FundEventsContext);
  return {logFundVisibilityIfShownFirstTime, logFundSelection};
};

export interface FundEventData {
  fundIdn: number,
  productType: ProductType,
  index: number,
}

export const useFundsVisibilityEventLogger = (screen: Screen, riskProfilerLevel?: number) => {
  const [fundsAlreadyDisplayed, setFundsAlreadyDisplayed] = useState<FundEventData[]>([]);
  const [,setLoggedFunds] = useState<FundEventData[]>([]);
  const funds = useSelector((state: ProductWizardState) => state.apiState.funds);

  const logFundVisibilityIfShownFirstTime = (fundIdn: number, productType: ProductType, index: number) => {
    setFundsAlreadyDisplayed(prevFundsDisplayed => {
      if (!(new Set(prevFundsDisplayed.map(value => value.fundIdn)).has(fundIdn))) {
        return [...prevFundsDisplayed, { fundIdn, productType, index }];
      } else {
        return prevFundsDisplayed;
      }
    });
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setLoggedFunds(prevLoggedFunds => {
        const fundsToLog = fundsAlreadyDisplayed.filter(displayed => !prevLoggedFunds.find(value => value.fundIdn === displayed.fundIdn));
        const newLoggedFunds = [...prevLoggedFunds, ...fundsToLog];
        if (fundsToLog.length !== 0) {
          const fullFundsToLog = fundsToLog.map(f => ({
            ...(funds.find(value => value.id === f.fundIdn))!,
            productType: f.productType,
            index: f.index,
          }));
          logViewItemEvent(fullFundsToLog, screen, riskProfilerLevel);
          return newLoggedFunds;
        }
        return prevLoggedFunds;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [fundsAlreadyDisplayed, setLoggedFunds]);

  return {logFundVisibilityIfShownFirstTime};
};

export const FundEventsContextProvider: React.FC<{screen: Screen}> = ({screen, children}) => {
  const {logFundVisibilityIfShownFirstTime} = useFundsVisibilityEventLogger(screen);
  const {logFundSelection} = useFundsSelectionEventLogger(screen);

  return (<FundEventsContext.Provider value={{logFundVisibilityIfShownFirstTime, logFundSelection}}>
        {children}
      </FundEventsContext.Provider>
  );
};

const useFundsSelectionEventLogger = (screen: Screen) => {
  const [,setFundsAlreadyDisplayed] = useState<number[]>([]);
  const funds = useSelector((state: ProductWizardState) => state.apiState.funds);

  const logFundSelection = (fundId: number, productType: ProductType, index: number, riskProfilerLevel?: number) => {
    setFundsAlreadyDisplayed(prevFundsDisplayed => {
      if (!(new Set(prevFundsDisplayed).has(fundId))) {
        const fund = funds.find(f => f.id === fundId);
        fund && logSelectItemEvent(fund, productType, screen, index, riskProfilerLevel);
        return [...prevFundsDisplayed, fundId];
      } else {
        return prevFundsDisplayed;
      }
    });
  };

  return {logFundSelection};
};
